import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import type { Practitioner } from '@/types/settings-api';

/**
 * Practitioner Personal Details — PROD-761.
 *
 * GET  /api/v1/Practice/Practitioner/{userId}  — fetch the Personal record
 * POST /api/Provider/Intake                    — save (body = same shape as GET)
 *
 * Round-trip is symmetric (verified 2026-05-13). The cURL revealed several
 * Flutter-side typo'd field names on the wire that must be preserved
 * verbatim: `orgnizationId`, `phonNo`, `identifire`. These look wrong but
 * they ARE the wire contract.
 */

/**
 * Canonical React Query key for the practitioner-by-id cache slot.
 *
 * Exported so cross-tab consumers (Billing tab reads the cached name
 * via the Personal-tab GET, per PR #132 review on cache-key fragility)
 * do not duplicate the magic string. Any rename here propagates to
 * every reader at compile time instead of silently degrading to the
 * cold-cache fallback. **Do NOT inline this array literal anywhere
 * else** — call this factory instead.
 */
export function practitionerByIdQueryKey(
  userId: string | null | undefined,
): readonly [string, string, string | null | undefined] {
  return ['settings', 'practitioner-by-id', userId];
}

export function usePractitionerById(userId: string | null | undefined) {
  return useQuery({
    queryKey: practitionerByIdQueryKey(userId),
    queryFn: ({ signal }) => {
      if (!userId) throw new Error('usePractitionerById called without userId');
      return get<Practitioner>(
        `/api/v1/Practice/Practitioner/${userId}`,
        { signal },
      );
    },
    enabled: !!userId,
  });
}

export function useSaveProviderIntake() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (profile: Practitioner) =>
      post<Practitioner>('/api/Provider/Intake', profile),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: practitionerByIdQueryKey(vars.userId),
      });
    },
  });
}
